import axios from 'axios';
import toast from './toastUtils';
import axiosRetry from 'axios-retry';
import { secureLogger } from './secureLogger';
import { tokenStorage } from './secureStorage';
import { getGlobalLogoutHandler } from '../contexts/AuthContext';

// =====================
// Axios Instance Setup
// =====================

// Determine base URL based on environment and local development flag
const getBaseURL = () => {
  const useLocal = process.env.REACT_APP_USE_LOCAL === 'true';
  const isProduction = process.env.NODE_ENV === 'production';
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // Production safeguard: Never use localhost in production builds
  if (isProduction && useLocal) {
    console.error('🚨 PRODUCTION SAFEGUARD: REACT_APP_USE_LOCAL=true detected in production build!');
    console.error('Forcing production URL to prevent localhost in production.');
    return process.env.REACT_APP_API_BASE_URL || 'https://backend-neoeloi-neoelois-projects.vercel.app/api';
  }
  
  // Use local development URL if flag is set OR if running on localhost
  if (useLocal || isLocalhost) {
    console.log('🔧 Using local development API URL: http://localhost:5000/api');
    return 'http://localhost:5000/api';
  }
  
  // Default to production URL
  return process.env.REACT_APP_API_BASE_URL || 'https://backend-neoeloi-neoelois-projects.vercel.app/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
  // If using HTTP-only cookies, uncomment the following line:
  // withCredentials: true,
});

// =====================
// Helper Functions
// =====================

/**
 * Retrieves the Device-ID from localStorage or generates a new one.
 * This ensures that Device-ID is always available in headers.
 */
const getDeviceId = () => {
  let deviceId = localStorage.getItem('deviceId');
  if (!deviceId) {
    deviceId = 'device-' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('deviceId', deviceId);
    secureLogger.info(`Generated new Device-ID: ${deviceId}`);
  }
  return deviceId;
};

// =====================
// Request Interceptor
// =====================
api.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    const deviceId = getDeviceId();
    config.headers['Device-ID'] = deviceId;
    return config;
  },
  (error) => Promise.reject(error)
);

// =====================
// Axios-Retry Configuration
// =====================
axiosRetry(api, {
  retries: 3, // Number of retry attempts
  retryDelay: (retryCount) => {
    const delay = axiosRetry.exponentialDelay(retryCount);
    secureLogger.info(`Retrying request... Attempt #${retryCount} in ${delay} ms`);
    return delay;
  },
  retryCondition: (error) => {
    // Retry on network errors or 5xx status codes
    return (
      axiosRetry.isNetworkOrIdempotentRequestError(error) ||
      (error.response &&
        error.response.status >= 500 &&
        error.response.status <= 599)
    );
  },
});

// =====================
// Response Interceptor
// =====================
api.interceptors.response.use(
  (response) => response, // Return 2xx responses directly
  (error) => {
    const { response, config } = error;

    // Allow requests to bypass global error toasts by setting this flag
    if (config && config.skipGlobalErrorHandler) {
      return Promise.reject(error);
    }

    if (!response) {
      toast.error('Network error. Please check your connection.');
      secureLogger.error('Network or CORS error:', error);
      return Promise.reject(error);
    }

    switch (response.status) {
      case 400:
        toast.error(response.data.message || 'Bad Request');
        break;
      case 401:
        // Token expired or invalid - trigger silent logout
        const logoutHandler = getGlobalLogoutHandler();
        if (logoutHandler) {
          secureLogger.info('401 Unauthorized - Token expired or invalid, logging out silently');
          logoutHandler();
        } else {
          // Fallback: clear token and redirect if handler not available
          tokenStorage.removeToken();
          window.location.href = '/login';
        }
        break;
      case 403:
        toast.error('You do not have permission to perform this action.');
        break;
      case 404:
        toast.error('Resource not found.');
        break;
      case 500:
        toast.error('Internal Server Error. Please try again later.');
        break;
      default:
        toast.error(response.data.message || 'An unexpected error occurred.');
    }

    secureLogger.error(`Error Response [${response.status}]:`, response.data);

    return Promise.reject(error);
  }
);

// =====================
// Exported Helper Functions
// =====================
export { getDeviceId };

// ========================================================
// 1. Authentication Management
// ========================================================
export const login = async (emailOrName, password) => {
  try {
    const deviceId = getDeviceId();
    secureLogger.info('Logging in with Device-ID:', deviceId);
    const response = await api.post('/auth/login', {
      emailOrName,
      password,
      deviceId,
    }, { skipGlobalErrorHandler: true });
    const { token, loggedOutDevices, user } = response.data;

    if (token) {
      tokenStorage.setToken(token);
      secureLogger.info('Token stored successfully');
    }

    // Device logout notifications removed

    return response.data;
  } catch (error) {
    secureLogger.error('Login API Error:', error);
    throw error;
  }
};

export const logout = async () => {
  try {
    const deviceId = getDeviceId();
    console.log('Logging out with Device-ID:', deviceId);
    const response = await api.post('/auth/logout', { deviceId });
    return response.data;
  } catch (error) {
    console.error('Logout API Error:', error);
    throw error;
  }
};

export const logoutDevice = async (deviceId) => {
  try {
    const response = await api.post(`/auth/logout`, { deviceId });
    return response.data;
  } catch (error) {
    console.error('Logout Device API Error:', error);
    throw error;
  }
};

// ========================================================
// 2. Subscription Management
// ========================================================
export const getSubscription = async () => {
  try {
    const response = await api.get('/profile/subscription');
    return response.data;
  } catch (error) {
    console.error('Get Subscription Error:', error);
    throw error;
  }
};

export const updateSubscriptionPlan = async (planType) => {
  try {
    const response = await api.put('/profile/subscription', { planType });
    return response.data;
  } catch (error) {
    console.error('Update Subscription Plan Error:', error);
    throw error;
  }
};

/**
 * Confirm payment and update subscription
 * Endpoint: POST /profile/payment-confirmation
 * Expects: { subscriptionType, paymentAmount, paymentMethod }
 */
export const confirmPayment = async (subscriptionType, paymentAmount, paymentMethod = 'Mobile Money') => {
  try {
    const response = await api.post('/profile/payment-confirmation', {
      subscriptionType,
      paymentAmount,
      paymentMethod
    });
    return response.data;
  } catch (error) {
    console.error('Confirm Payment Error:', error);
    throw error;
  }
};

export const cancelSubscription = async () => {
  try {
    const response = await api.put('/profile/subscription/cancel');
    return response.data;
  } catch (error) {
    console.error('Cancel Subscription Error:', error);
    throw error;
  }
};

export const resumeSubscription = async () => {
  try {
    const response = await api.put('/profile/subscription/resume');
    return response.data;
  } catch (error) {
    console.error('Resume Subscription Error:', error);
    throw error;
  }
};

// ========================================================
// 3. Admin Subscription Management (Optional)
// ========================================================
export const adminUpdateUserSubscription = async (userId, planType) => {
  try {
    const response = await api.put(`/admin/users/${userId}/subscription`, { planType });
    return response.data;
  } catch (error) {
    console.error('Admin Update User Subscription Error:', error);
    throw error;
  }
};

export const adminCancelUserSubscription = async (userId) => {
  try {
    const response = await api.put(`/admin/users/${userId}/subscription/cancel`);
    return response.data;
  } catch (error) {
    console.error('Admin Cancel User Subscription Error:', error);
    throw error;
  }
};

export const adminResumeUserSubscription = async (userId) => {
  try {
    const response = await api.put(`/admin/users/${userId}/subscription/resume`);
    return response.data;
  } catch (error) {
    console.error('Admin Resume User Subscription Error:', error);
    throw error;
  }
};

// ========================================================
// 4. Payment Methods Management
// ========================================================
export const getPaymentMethods = async () => {
  try {
    const response = await api.get('/profile/payment-methods');
    return response.data;
  } catch (error) {
    console.error('Get Payment Methods Error:', error);
    throw error;
  }
};

export const addPaymentMethod = async (type, details) => {
  try {
    const response = await api.post('/profile/payment-methods', { type, details });
    return response.data;
  } catch (error) {
    console.error('Add Payment Method Error:', error);
    throw error;
  }
};

export const deletePaymentMethod = async (id) => {
  try {
    const response = await api.delete(`/profile/payment-methods/${id}`);
    return response.data;
  } catch (error) {
    console.error('Delete Payment Method Error:', error);
    throw error;
  }
};

export const setDefaultPaymentMethod = async (id) => {
  try {
    const response = await api.put(`/profile/payment-methods/${id}/set-default`);
    return response.data;
  } catch (error) {
    console.error('Set Default Payment Method Error:', error);
    throw error;
  }
};

// ========================================================
// 5. Billing History
// ========================================================
export const getBillingHistory = async () => {
  try {
    const response = await api.get('/profile/billing-history');
    return response.data;
  } catch (error) {
    console.error('Get Billing History Error:', error);
    throw error;
  }
};

// ========================================================
// 6. Profile Management
// ========================================================
export const getUserProfile = async () => {
  try {
    const response = await api.get('/profile');
    return response.data;
  } catch (error) {
    console.error('Get User Profile Error:', error);
    throw error;
  }
};

export const updateUserProfile = async (profileData) => {
  try {
    const response = await api.put('/profile', profileData);
    return response.data;
  } catch (error) {
    console.error('Update User Profile Error:', error);
    throw error;
  }
};

export const updateUserPassword = async (currentPassword, newPassword) => {
  try {
    const response = await api.put('/profile/password', { currentPassword, newPassword });
    return response.data;
  } catch (error) {
    console.error('Update User Password Error:', error);
    throw error;
  }
};

// ========================================================
// 7. Device Management
// ========================================================
export const getDevices = async () => {
  try {
    const response = await api.get('/profile/devices');
    return response.data;
  } catch (error) {
    console.error('Get Devices Error:', error);
    throw error;
  }
};

export const allowDevice = async (deviceId) => {
  try {
    const response = await api.post(`/profile/devices/${deviceId}/allow`);
    return response.data;
  } catch (error) {
    console.error('Allow Device Error:', error);
    throw error;
  }
};

export const blockDevice = async (deviceId) => {
  try {
    const response = await api.post(`/profile/devices/${deviceId}/block`);
    return response.data;
  } catch (error) {
    console.error('Block Device Error:', error);
    throw error;
  }
};

export const removeDevice = async (deviceId) => {
  try {
    const response = await api.delete(`/profile/devices/${deviceId}`);
    return response.data;
  } catch (error) {
    console.error('Remove Device Error:', error);
    throw error;
  }
};

// ========================================================
// 8. Quiz Progress Management
// ========================================================
export const saveUserQuizProgress = async (quizType, progressData) => {
  try {
    const response = await api.post('/quiz/progress', { quizType, ...progressData });
    return response.data;
  } catch (error) {
    console.error('Save Quiz Progress Error:', error);
    throw error;
  }
};

export const getUserQuizStats = async (quizType) => {
  try {
    const query = quizType ? `?quizType=${encodeURIComponent(quizType)}` : '';
    const response = await api.get(`/quiz/stats${query}`);
    return response.data;
  } catch (error) {
    console.error('Get Quiz Stats Error:', error);
    throw error;
  }
};

export const getLatestQuizzesFromServer = async (limit = 3, page = 1) => {
  try {
    const response = await api.get(`/quiz/history`, { params: { limit, page } });
    return response.data.history || [];
  } catch (error) {
    console.error('Get Latest Quizzes Error:', error);
    throw error;
  }
};

export const getAggregatedQuizStats = async (params = {}) => {
  try {
    const response = await api.get('/quiz/aggregated-stats', { params });
    return response.data;
  } catch (error) {
    console.error('Get Aggregated Quiz Stats Error:', error);
    throw error;
  }
};

// ========================================================
// 9. Course Progress Management
// ========================================================
export const saveCourseProgress = async (courseType, progressData) => {
  try {
    const response = await api.post(`/course/progress/${encodeURIComponent(courseType)}`, progressData);
    return response.data;
  } catch (error) {
    console.error('Save Course Progress Error:', error);
    throw error;
  }
};

export const getCourseProgress = async (courseType) => {
  try {
    const response = await api.get(`/course/progress/${encodeURIComponent(courseType)}`);
    return response.data;
  } catch (error) {
    console.error('Get Course Progress Error:', error);
    throw error;
  }
};

export const getAllCourseProgress = async () => {
  try {
    const response = await api.get('/course/progress');
    return response.data;
  } catch (error) {
    console.error('Get All Course Progress Error:', error);
    throw error;
  }
};

// ========================================================
// 10. Notification Management
// ========================================================
export const getNotifications = async () => {
  try {
    const response = await api.get('/notifications');
    return response.data;
  } catch (error) {
    console.error('Get Notifications Error:', error);
    throw error;
  }
};

export const createNotification = async (message, type = 'info') => {
  try {
    const response = await api.post('/notifications', { message, type });
    return response.data.notification;
  } catch (error) {
    console.error('Create Notification Error:', error);
    throw error;
  }
};

export const updateNotification = async (notificationId, updates) => {
  try {
    const response = await api.patch(`/notifications/${notificationId}`, updates);
    return response.data.notification;
  } catch (error) {
    console.error('Update Notification Error:', error);
    throw error;
  }
};

export const dismissNotification = async (notificationId) => {
  try {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  } catch (error) {
    console.error('Dismiss Notification Error:', error);
    throw error;
  }
};

export const markAllNotificationsAsRead = async () => {
  try {
    const response = await api.patch('/notifications/mark-all-read');
    return response.data;
  } catch (error) {
    console.error('Mark All Notifications As Read Error:', error);
    throw error;
  }
};

// ========================================================
// 11. Admin User Management
// ========================================================
export const getAllUsers = async (page = 1, limit = 50, sortOption = 'date', searchQuery = '') => {
  try {
    const params = { page, limit, sortBy: sortOption, search: searchQuery };
    const response = await api.get('/admin/users', { params });
    return response.data;
  } catch (error) {
    console.error('Get All Users Error:', error);
    throw error;
  }
};

export const getUserById = async (userId) => {
  try {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Get User By ID Error:', error);
    throw error;
  }
};

export const createUserAdmin = async (userData) => {
  try {
    const response = await api.post('/admin/users', userData);
    return response.data;
  } catch (error) {
    console.error('Create User Error:', error);
    throw error;
  }
};

export const updateUserAdmin = async (userId, updateData) => {
  try {
    const response = await api.put(`/admin/users/${userId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('Update User Error:', error);
    throw error;
  }
};

export const deleteUserAdmin = async (userId) => {
  try {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Delete User Error:', error);
    throw error;
  }
};

// ========================================================
// 12. Admin Metrics Management
// ========================================================
export const getAdminMetrics = async () => {
  try {
    const response = await api.get('/admin/metrics');
    return response.data;
  } catch (error) {
    console.error('Get Admin Metrics Error:', error);
    throw error;
  }
};

// ========================================================
// 13. Notification Management for Admins
// ========================================================
export const getAllNotificationsAdmin = async (page = 1, limit = 50) => {
  try {
    const response = await api.get('/admin/notifications', { params: { page, limit } });
    return response.data;
  } catch (error) {
    console.error('Get All Notifications (Admin) Error:', error);
    throw error;
  }
};

export const createNotificationAdmin = async (notificationData) => {
  try {
    const response = await api.post('/admin/notifications', notificationData);
    return response.data.notification;
  } catch (error) {
    console.error('Create Notification (Admin) Error:', error);
    throw error;
  }
};

export const updateNotificationAdmin = async (notificationId, updates) => {
  try {
    const response = await api.put(`/admin/notifications/${notificationId}`, updates);
    return response.data.notification;
  } catch (error) {
    console.error('Update Notification (Admin) Error:', error);
    throw error;
  }
};

export const deleteNotificationAdmin = async (notificationId) => {
  try {
    const response = await api.delete(`/admin/notifications/${notificationId}`);
    return response.data;
  } catch (error) {
    console.error('Delete Notification (Admin) Error:', error);
    throw error;
  }
};

// ========================================================
// 14. Report Management (Embedded in User Model)
// ========================================================
/**
 * Fetch all embedded reports with pagination, sorting, and filtering (Admin Only).
 * The backend now returns reports embedded within user documents.
 * @param {number} [page=1] - Page number.
 * @param {number} [limit=50] - Number of reports per page.
 * @param {string} [sortBy='createdAt'] - Sorting field.
 * @param {string} [search=''] - Search query for title or description.
 * @param {Object} [filters={}] - Additional filters (e.g., category, priority, status).
 * @returns {Object} - { page, limit, totalPages, totalReports, reports }
 */
export const getReports = async (page = 1, limit = 50, sortBy = 'createdAt', search = '', filters = {}) => {
  try {
    const params = { page, limit, sortBy, search, ...filters };
    const response = await api.get('/admin/reports', { params });
    return response.data;
  } catch (error) {
    console.error('Get Reports Error:', error);
    throw error;
  }
};

/**
 * Fetch a single embedded report by ID (Admin Only).
 * @param {string} reportId - The ID of the report to fetch.
 * @returns {Object} - { report }
 */
export const getReportById = async (reportId) => {
  try {
    const response = await api.get(`/admin/reports/${reportId}`);
    return response.data.report;
  } catch (error) {
    console.error('Get Report By ID Error:', error);
    throw error;
  }
};

/**
 * Update the status of an embedded report (Admin Only).
 * @param {string} reportId - The ID of the report to update.
 * @param {string} status - The new status ('open', 'inProgress', 'resolved', 'closed').
 * @returns {Object} - Updated report details.
 */
export const updateReportStatus = async (reportId, status) => {
  try {
    const response = await api.put(`/admin/reports/${reportId}/status`, { status });
    return response.data.report;
  } catch (error) {
    console.error('Update Report Status Error:', error);
    throw error;
  }
};

/**
 * Assign an embedded report to an admin (Admin Only).
 * @param {string} reportId - The ID of the report to assign.
 * @param {string} adminId - The ID of the admin.
 * @returns {Object} - Updated report details.
 */
export const assignReport = async (reportId, adminId) => {
  try {
    const response = await api.put(`/admin/reports/${reportId}/assign`, { adminId });
    return response.data.report;
  } catch (error) {
    console.error('Assign Report Error:', error);
    throw error;
  }
};

/**
 * Delete an embedded report (Admin Only).
 * @param {string} reportId - The ID of the report to delete.
 * @returns {Object} - Confirmation message.
 */
export const deleteReport = async (reportId) => {
  try {
    const response = await api.delete(`/admin/reports/${reportId}`);
    return response.data;
  } catch (error) {
    console.error('Delete Report Error:', error);
    throw error;
  }
};

/**
 * Submit a new problem report (for regular users).
 * This will call the support endpoint that uses the User model's embedded reports.
 * @param {Object} reportData - { title, description, priority, category }
 * @returns {Object} - Created report details.
 */
export const submitReport = async (reportData) => {
  try {
    const response = await api.post('/support/report', reportData);
    return response.data.report;
  } catch (error) {
    console.error('Submit Report Error:', error);
    throw error;
  }
};

// ========================================================
// 15. Aggregated Data Endpoints for Reports Charts / Metrics
// ========================================================
/**
 * Fetch aggregated data for user reports.
 * Expected format: An array of objects, e.g.,
 * [ { name: 'Technical', value: 400 }, { name: 'Account', value: 300 }, ... ]
 */
export const getAggregatedUserReports = async () => {
  try {
    const response = await api.get('/admin/reports/aggregated/userReports');
    return response.data; // e.g., { data: [...] }
  } catch (error) {
    console.error('Error fetching aggregated user reports:', error);
    throw error;
  }
};

/**
 * Fetch aggregated metrics for reports.
 * Expected response: { total: Number, viewed: Number, responded: Number }
 */
export const getAggregatedReportMetrics = async () => {
  try {
    const response = await api.get('/admin/reports/aggregated/metrics');
    return response.data; // e.g., { total: 123, viewed: 45, responded: 67 }
  } catch (error) {
    console.error('Error fetching aggregated report metrics:', error);
    throw error;
  }
};

/**
 * Send feedback (message) to the user for a specific report.
 * This should push a notification only to the user who submitted the report.
 * @param {string} reportId - The ID of the report.
 * @param {string} message - The feedback message.
 */
export const sendReportFeedback = async (reportId, message) => {
  try {
    const response = await api.post(`/admin/reports/${reportId}/feedback`, { message });
    return response.data;
  } catch (error) {
    console.error('Error sending feedback for report:', error);
    throw error;
  }
};

// ========================================================
// 16. Support Email Management
// ========================================================
/**
 * Send a support email.
 * This function calls the new endpoint /support/send-email with the sender's email and message.
 * @param {string} email - The sender's email address.
 * @param {string} message - The support message.
 * @returns {Object} - API response data.
 */
export const sendSupportEmail = async (email, message) => {
  try {
    const response = await api.post('/support/send-email', { email, message });
    return response.data;
  } catch (error) {
    console.error('Send Support Email Error:', error);
    throw error;
  }
};

// =====================
// 17. Agent Management (Updated)
// =====================

/**
 * Submit an agent application.
 * Endpoint: POST /agents/apply
 * Expects: { name, phone, email, message }
 */
export const submitAgentApplication = async (applicationData) => {
  try {
    const response = await api.post('/agents/apply', applicationData);
    return response.data;
  } catch (error) {
    console.error('Submit Agent Application Error:', error);
    throw error;
  }
};

/**
 * Get all agent applications.
 * Endpoint: GET /agents/applications
 * Returns an array of applications.
 */
export const getAgentApplications = async () => {
  try {
    const response = await api.get('/agents/applications');
    return response.data;
  } catch (error) {
    console.error('Get Agent Applications Error:', error);
    throw error;
  }
};

/**
 * Update an agent application.
 * Endpoint: PUT /agents/applications/:userId
 * Expects: { adminStatus } where adminStatus can be 'approved' or 'rejected'.
 * Note: If approved, the backend will update the active agent details (with name, email, phone)
 *       and remove the agent application sub-document.
 */
export const updateAgentApplication = async (userId, updateData) => {
  try {
    const response = await api.put(`/agents/applications/${userId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('Update Agent Application Error:', error);
    throw error;
  }
};

/**
 * Get all active agents.
 * Endpoint: GET /agents
 * Returns an array of active agent details.
 */
export const getActiveAgents = async () => {
  try {
    const response = await api.get('/agents');
    return response.data;
  } catch (error) {
    console.error('Get Active Agents Error:', error);
    throw error;
  }
};

/**
 * Get a specific active agent by ID.
 * Endpoint: GET /agents/:agentId
 */
export const getAgentById = async (agentId) => {
  try {
    const response = await api.get(`/agents/${agentId}`);
    return response.data;
  } catch (error) {
    console.error('Get Agent By Id Error:', error);
    throw error;
  }
};

/**
 * Get the currently authenticated agent's details.
 * Endpoint: GET /agents/me
 */
export const getMyAgentData = async () => {
  try {
    const response = await api.get('/agents/me');
    return response.data;
  } catch (error) {
    console.error('Get My Agent Data Error:', error);
    throw error;
  }
};

/**
 * Get metrics for the currently authenticated agent (for dashboard).
 * Endpoint: GET /agents/me
 * Returns detailed metrics including time-series data for graphs.
 */
export const getAgentMetrics = async () => {
  try {
    const response = await api.get('/agents/me');
    return response.data;
  } catch (error) {
    console.error('Error fetching agent metrics:', error);
    throw error;
  }
};
export const getAgentRecords = async () => {
  try {
    const response = await api.get('/agents/me/records');
    return response.data.transactions;
  } catch (error) {
    console.error('Get Agent Records Error:', error);
    throw error;
  }
};
// =====================
// 18. Export the Axios Instance
// =====================
export default api;

/**
 * Request a cash out for the current agent.
 * Endpoint: POST /agents/cashout
 */
export const requestCashOut = async () => {
  try {
    const response = await api.post('/agents/cashout');
    return response.data;
  } catch (error) {
    console.error('Cash Out Request Error:', error);
    throw error;
  }
};

/**
 * Process agent withdrawal (Admin only).
 * Endpoint: POST /admin/agents/:agentId/withdraw
 * @param {string} agentId - The ID of the agent to process withdrawal for
 */
export const processAgentWithdrawal = async (agentId) => {
  try {
    const response = await api.post(`/admin/agents/${agentId}/withdraw`);
    return response.data;
  } catch (error) {
    console.error('Process Agent Withdrawal Error:', error);
    throw error;
  }
};

// ========================================================
// 15. Moderator API Functions
// ========================================================

/**
 * Fetch agent applications (Moderator)
 * Endpoint: GET /moderator/agent-applications
 */
export const getModeratorAgentApplications = async () => {
  try {
    const response = await api.get('/moderator/agent-applications');
    return response.data;
  } catch (error) {
    console.error('Get Moderator Agent Applications Error:', error);
    throw error;
  }
};


/**
 * Update agent application status (approve/reject) (Moderator)
 * Endpoint: PUT /moderator/agent-applications/:userId
 * @param {string} userId - User ID
 * @param {Object} data - Update data (adminStatus)
 */
export const updateModeratorAgentApplication = async (userId, data) => {
  try {
    const response = await api.put(`/moderator/agent-applications/${userId}`, data);
    return response.data;
  } catch (error) {
    console.error('Update Moderator Agent Application Error:', error);
    throw error;
  }
};

/**
 * Fetch active agents (read-only) (Moderator)
 * Endpoint: GET /moderator/active-agents
 */
export const getModeratorActiveAgents = async () => {
  try {
    const response = await api.get('/moderator/active-agents');
    return response.data;
  } catch (error) {
    console.error('Get Moderator Active Agents Error:', error);
    throw error;
  }
};

/**
 * Fetch moderator agents overview (applications + active)
 * Endpoint: GET /moderator/agents-overview
 */
export const getModeratorAgentsOverview = async () => {
  try {
    const response = await api.get('/moderator/agents-overview');
    return response.data;
  } catch (error) {
    console.error('Get Moderator Agents Overview Error:', error);
    throw error;
  }
};

// ========================================================
// Subscription Requests Management
// ========================================================

/**
 * Create a subscription request
 * Endpoint: POST /subscription-requests
 * @param {Object} data - Request data { requestedPlan, phone }
 */
export const createSubscriptionRequest = async (data) => {
  try {
    const response = await api.post('/subscription-requests', data);
    return response.data;
  } catch (error) {
    console.error('Create Subscription Request Error:', error);
    throw error;
  }
};

/**
 * Get all subscription requests (Admin/Moderator only)
 * Endpoint: GET /subscription-requests
 * @param {Object} filters - Optional filters { status, search, sortBy, order }
 */
export const getSubscriptionRequests = async (filters = {}) => {
  try {
    const response = await api.get('/subscription-requests', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Get Subscription Requests Error:', error);
    throw error;
  }
};

/**
 * Approve a subscription request (Admin/Moderator only)
 * Endpoint: PUT /subscription-requests/:id/approve
 * @param {string} id - Request ID
 */
export const approveSubscriptionRequest = async (id) => {
  try {
    const response = await api.put(`/subscription-requests/${id}/approve`, {});
    return response.data;
  } catch (error) {
    console.error('Approve Subscription Request Error:', error);
    throw error;
  }
};

/**
 * Reject a subscription request (Admin/Moderator only)
 * Endpoint: PUT /subscription-requests/:id/reject
 * @param {string} id - Request ID
 */
export const rejectSubscriptionRequest = async (id) => {
  try {
    const response = await api.put(`/subscription-requests/${id}/reject`, {});
    return response.data;
  } catch (error) {
    console.error('Reject Subscription Request Error:', error);
    throw error;
  }
};

/**
 * Delete a subscription request (Admin/Moderator only, rejected requests only)
 * Endpoint: DELETE /subscription-requests/:id
 * @param {string} id - Request ID
 */
export const deleteSubscriptionRequest = async (id) => {
  try {
    const response = await api.delete(`/subscription-requests/${id}`);
    return response.data;
  } catch (error) {
    console.error('Delete Subscription Request Error:', error);
    throw error;
  }
};

/**
 * Get current user's own subscription requests
 * Endpoint: GET /subscription-requests/my-requests
 */
export const getMySubscriptionRequests = async () => {
  try {
    const response = await api.get('/subscription-requests/my-requests');
    return response.data;
  } catch (error) {
    console.error('Get My Subscription Requests Error:', error);
    throw error;
  }
};